import _ from 'lodash'
import {
  ExcelRow,
  ProcessCellForExportParams,
  GridApi,
} from 'ag-grid-community'
import { MutableRefObject, useCallback, useMemo } from 'react'
import { DateTerm } from '../../../../utils/date'
import { intl } from '../../../../i18n'
import { CustomEnumValueDetail } from '../../../../lib/functions/customEnumValue'
import { getLabel } from '../../../../lib/commons/i18nLabel'
import {
  ActualWorkingHoursRow,
  StandardWorkingInfo,
  convertBreakTime,
  formatHour,
} from '../ActualWorkingHours'
import { exportExcel } from '../../../containers/BulkSheet/excel'
import { dateVoService } from '../../../../domain/value-object/DateVO'
import DateVO from '../../../../vo/DateVO'
import { featureFlagService } from '../../../../utils/feature-flag'

const EXCEL_COLUMN_START_TIME: string = 'D'
const EXCEL_COLUMN_END_TIME: string = 'E'
const EXCEL_COLUMN_BREAK_TIME: string = 'F'
const EXCEL_COLUMN_WORKED_TIME: string = 'G'
const EXCEL_START_INDEX_OFFSET: number = 4 // header row + next

const EXCEL_CELL_SUM_ACTUAL_DAYS: string = 'E4'
const EXCEL_CELL_SUM_SCHEDULE_DAYS: string = 'D4'
const EXCEL_CELL_SUM_ACTUAL_TIME: string = 'E5'
const EXCEL_CELL_SUM_SCHEDULE_TIME: string = 'D5'
const SUMMARY_START_INDEX: number = 9

const SECOND_TO_EXCEL_SERIAL_VALUE: number = 3600 * 24

type ExcelHeaderContents = {
  dateTerm: DateTerm
  userName: string | undefined
  standardInfo: StandardWorkingInfo
}

export const useExcelExport = (
  contextRef: MutableRefObject<Record<string, any>>,
  gridApi: GridApi,
  headerContents: ExcelHeaderContents,
  isConfirm: boolean = false
): (() => void) => {
  const getTotalValueFormula = useCallback(
    (column: string, startRow: number, endRow: number): string => {
      return `=SUM(${column}${startRow}:${column}${endRow})`
    },
    []
  )

  const convertSlashFormat = useCallback((value: string | undefined) => {
    if (!value) return ''
    const date = new DateVO(value)
    return date.format('YYYY/M/D')
  }, [])

  const PERCENTAGE_FORMULA_TEMPLATE = (actual: string, schedule: string) =>
    `=IF(${schedule}=0,"",ROUND((${actual}-${schedule})/${schedule}*100,2) & "%")`

  const { fileNamePrefix, prependContent, outputNumOfDays } = useMemo(() => {
    const fileNamePrefix = headerContents.userName
      ? intl.formatMessage(
          {
            id: 'actualWorkingHours.excel.name.prefix.format',
          },
          {
            name: headerContents.userName,
          }
        )
      : intl.formatMessage({
          id: 'actualWorkingHours.excel.title',
        })

    let outputNumOfDays = 0
    if (
      !_.isEmpty(headerContents.dateTerm.startDate) &&
      !_.isEmpty(headerContents.dateTerm.endDate)
    ) {
      const start = dateVoService.construct(headerContents.dateTerm.startDate)
      const end = dateVoService.construct(headerContents.dateTerm.endDate)
      outputNumOfDays = Math.abs(dateVoService.diff(start, end)) + 1
    }
    const prependContent: ExcelRow[] = [
      {
        cells: [],
      },
      {
        cells: [
          {
            data: {
              value: '',
              type: 'String',
            },
          },
          {
            data: {
              value: intl.formatMessage({
                id: 'actualWorkingHours.excel.user',
              }),
              type: 'String',
            },
            mergeAcross: 1,
          },
          {
            data: {
              value: headerContents.userName || '',
              type: 'String',
            },
          },
        ],
      },
      {
        cells: [
          {
            data: {
              value: '',
              type: 'String',
            },
          },
          {
            data: {
              value: intl.formatMessage({
                id: 'actualWorkingHours.header.term',
              }),
              type: 'String',
            },
            mergeAcross: 1,
          },
          {
            styleId: 'justifyRight',
            data: {
              value: convertSlashFormat(headerContents.dateTerm.startDate),
              type: 'String',
            },
          },
          {
            styleId: 'centerCell',
            data: {
              value: '～',
              type: 'String',
            },
          },
          {
            styleId: 'justifyRight',
            data: {
              value: convertSlashFormat(headerContents.dateTerm.endDate),
              type: 'String',
            },
          },
        ],
      },
      {
        cells: [
          {
            data: {
              value: '',
              type: 'String',
            },
          },
          {
            mergeAcross: 1,
            data: {
              value: intl.formatMessage({
                id: 'actualWorkingHours.header.days',
              }),
              type: 'String',
            },
          },
          {
            data: {
              value: String(headerContents.standardInfo.standardWorkDays),
              type: 'Number',
            },
          },
          {
            styleId: 'formulaCell',
            data: {
              value: featureFlagService.isEnabled(
                'task-actual-work-application'
              )
                ? `=COUNTIFS(${EXCEL_COLUMN_START_TIME}${isConfirm ? SUMMARY_START_INDEX + 1 : SUMMARY_START_INDEX + 2}:${EXCEL_COLUMN_START_TIME}${
                    isConfirm
                      ? outputNumOfDays + SUMMARY_START_INDEX
                      : outputNumOfDays + SUMMARY_START_INDEX + 1
                  },"<>",${EXCEL_COLUMN_END_TIME}${isConfirm ? SUMMARY_START_INDEX + 1 : SUMMARY_START_INDEX + 2}:${EXCEL_COLUMN_END_TIME}${
                    isConfirm
                      ? outputNumOfDays + SUMMARY_START_INDEX
                      : outputNumOfDays + SUMMARY_START_INDEX + 1
                  },"<>")`
                : `=COUNTIFS(${EXCEL_COLUMN_START_TIME}${SUMMARY_START_INDEX}:${EXCEL_COLUMN_START_TIME}${
                    outputNumOfDays + SUMMARY_START_INDEX - 1
                  },"<>",${EXCEL_COLUMN_END_TIME}${SUMMARY_START_INDEX}:${EXCEL_COLUMN_END_TIME}${
                    outputNumOfDays + SUMMARY_START_INDEX - 1
                  },"<>")`,
              type: 'Formula',
            },
          },
          {
            styleId: 'formulaCell',
            data: {
              value: `=${EXCEL_CELL_SUM_ACTUAL_DAYS}-${EXCEL_CELL_SUM_SCHEDULE_DAYS}`,
              type: 'Formula',
            },
          },
          {
            styleId: 'formulaTimeCell',
            data: {
              value: PERCENTAGE_FORMULA_TEMPLATE(
                EXCEL_CELL_SUM_ACTUAL_DAYS,
                EXCEL_CELL_SUM_SCHEDULE_DAYS
              ),

              type: 'Formula',
            },
          },
        ],
      },
      {
        cells: [
          {
            data: {
              value: '',
              type: 'String',
            },
          },
          {
            mergeAcross: 1,
            data: {
              value: intl.formatMessage({
                id: 'actualWorkingHours.header.hours',
              }),
              type: 'String',
            },
          },
          {
            styleId: 'justifyRight',
            data: {
              value: formatHour(headerContents.standardInfo.standardWorkHours),
              type: 'String',
            },
          },
          {
            styleId: 'formulaTimeCell',
            data: {
              value: featureFlagService.isEnabled(
                'task-actual-work-application'
              )
                ? getTotalValueFormula(
                    EXCEL_COLUMN_WORKED_TIME,
                    isConfirm
                      ? SUMMARY_START_INDEX + 1
                      : SUMMARY_START_INDEX + 2,
                    isConfirm
                      ? outputNumOfDays + SUMMARY_START_INDEX
                      : outputNumOfDays + SUMMARY_START_INDEX + 1
                  )
                : getTotalValueFormula(
                    EXCEL_COLUMN_WORKED_TIME,
                    SUMMARY_START_INDEX,
                    outputNumOfDays + SUMMARY_START_INDEX - 1
                  ),
              type: 'Formula',
            },
          },
          {
            styleId: 'formulaTimeCell',
            data: {
              value: `=TEXT(ABS(TEXT(${EXCEL_CELL_SUM_ACTUAL_TIME}, "[h]:mm")-TEXT(${EXCEL_CELL_SUM_SCHEDULE_TIME}, "[h]:mm")),IF(VALUE(TEXT(${EXCEL_CELL_SUM_ACTUAL_TIME},"[h]:mm")-TEXT(${EXCEL_CELL_SUM_SCHEDULE_TIME},"[h]:mm"))<0,"-[h]:mm","[h]:mm"))`,
              type: 'Formula',
            },
          },
          {
            styleId: 'formulaTimeCell',
            data: {
              value: PERCENTAGE_FORMULA_TEMPLATE(
                EXCEL_CELL_SUM_ACTUAL_TIME,
                EXCEL_CELL_SUM_SCHEDULE_TIME
              ),
              type: 'Formula',
            },
          },
        ],
      },
      {
        cells: [],
      },
    ]
    return { fileNamePrefix, prependContent, outputNumOfDays }
  }, [convertSlashFormat, getTotalValueFormula, headerContents])

  const workDayTypeNames = useMemo(() => {
    const types: CustomEnumValueDetail[] =
      contextRef.current['workingHoursType']
    if (!types) return []
    const names = types
      .filter(
        t => t.value && ['ATTENDANCE', 'PAID_LEAVE_HALF'].includes(t.value)
      )
      .map(t => getLabel(t.nameI18n) ?? t.name)
    return names
  }, [contextRef])

  const customExportValues = useCallback(
    (params: ProcessCellForExportParams): string | undefined => {
      if (
        !params.node ||
        params.node.rowIndex === null ||
        _.isEmpty(workDayTypeNames)
      ) {
        return undefined
      }
      const rowIndex = featureFlagService.isEnabled(
        'task-actual-work-application'
      )
        ? prependContent.length +
          EXCEL_START_INDEX_OFFSET +
          (isConfirm ? params.node.rowIndex - 1 : params.node.rowIndex)
        : prependContent.length +
          EXCEL_START_INDEX_OFFSET +
          params.node.rowIndex -
          1
      const maxRowIndex = featureFlagService.isEnabled(
        'task-actual-work-application'
      )
        ? prependContent.length +
          EXCEL_START_INDEX_OFFSET +
          (isConfirm ? outputNumOfDays - 1 : outputNumOfDays)
        : prependContent.length + EXCEL_START_INDEX_OFFSET + outputNumOfDays - 2

      const colId: string = params.column.getColId()
      const data = params.node.data
      if (!data) return ''
      if (colId === 'actual.breakTime') {
        if (data.type === 'SUM') {
          return getTotalValueFormula(
            EXCEL_COLUMN_BREAK_TIME,
            rowIndex,
            maxRowIndex
          )
        } else if (params.node?.data) {
          // TODO: If you put a VALUE expression in the total column, the range will be #SPILL and an error will occur
          const row = params.node.data as ActualWorkingHoursRow
          if (!row.actual?.breakTime) return ''
          const breakTime = convertBreakTime(row)
          return (breakTime / SECOND_TO_EXCEL_SERIAL_VALUE).toString()
        }
      } else if (colId === 'actual.workedTime') {
        if (data.type === 'SUM') {
          return getTotalValueFormula(
            EXCEL_COLUMN_WORKED_TIME,
            rowIndex,
            maxRowIndex
          )
        }
        const startTimeCell = featureFlagService.isEnabled(
          'task-actual-work-application'
        )
          ? EXCEL_COLUMN_START_TIME + (rowIndex + 1)
          : EXCEL_COLUMN_START_TIME + rowIndex
        const endTimeCell = featureFlagService.isEnabled(
          'task-actual-work-application'
        )
          ? EXCEL_COLUMN_END_TIME + (rowIndex + 1)
          : EXCEL_COLUMN_END_TIME + rowIndex
        const breakTimeCell = featureFlagService.isEnabled(
          'task-actual-work-application'
        )
          ? EXCEL_COLUMN_BREAK_TIME + (rowIndex + 1)
          : EXCEL_COLUMN_BREAK_TIME + rowIndex
        const endTime = `IF(VALUE(${endTimeCell}) < VALUE(${startTimeCell}), ${endTimeCell} + "24:00", ${endTimeCell})`
        // If you use the LET function, you will get an error with a spill avoidance "@"
        const formula = `=IF(OR(ISBLANK(${startTimeCell}), ISBLANK(${endTimeCell})), "", 
          IF((${endTime} - ${startTimeCell} - ${breakTimeCell}) > 0, ${endTime} - ${startTimeCell} - ${breakTimeCell}, ""))`
        return formula
      }
      return undefined
    },
    [
      prependContent.length,
      workDayTypeNames,
      outputNumOfDays,
      getTotalValueFormula,
    ]
  )

  const onExportExcel = useCallback(() => {
    const visibleColIds =
      gridApi
        ?.getColumnState()
        .filter(state => !state.hide)
        .map(state => state.colId) || []
    const colIds = (gridApi?.getColumns() ?? [])
      .filter(
        column =>
          column.getColId() === 'blank' ||
          (!['uuid'].includes(column.getColId()) &&
            visibleColIds.includes(column.getColId()))
      )
      .sort((colA, colB) => colA.getInstanceId() - colB.getInstanceId())
    if (!colIds || colIds.length === 0) return

    exportExcel({
      fileNamePrefix,
      gridApi,
      exportColIds: colIds.map(v => v.getColId()),
      prependContent,
      getCustomExportValue: customExportValues,
    })
  }, [gridApi, fileNamePrefix, prependContent, customExportValues])

  return onExportExcel
}
